import useIsMobile from 'utils/hooks/useIsMobile'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {SortableContext, arrayMove, rectSortingStrategy, useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation'
import HelpIcon from '@mui/icons-material/Help'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Backdrop,
  Button,
  Chip,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import {styled} from '@mui/material/styles'
import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro'
import _ from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {HyphensOfficeHandler} from 'utils/const/globalConst'
import {outerInstallShowHandler, showBusanExcelHandler} from 'utils/const/globalController'
import {envBackHost, serverLocalName} from '../../GLOBAL/envVariables'
import {axiosWithAuth} from '../../helper/axiosWithAuth'
import {resultAlert, validationAlert} from '../../utils/CustomAlert/Alert'

const ListItem = styled('li')(({theme}) => ({
  margin: theme.spacing(0.5),
}))

/*────────────────────────────────────────
  드래그 가능한 Chip 컴포넌트
────────────────────────────────────────*/
const SortableChip = ({data, onDelete}: {data: any; onDelete: () => void}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: data.idx,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none',
  }

  return (
    <ListItem ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip
        label={data?.label}
        onDelete={onDelete}
        // onDelete 클릭 시 drag 이벤트가 같이 발생하지 않도록
        onMouseDown={(e) => e.stopPropagation()}
        sx={{fontSize: '16px', cursor: 'grab'}}
      />
    </ListItem>
  )
}

/*────────────────────────────────────────
  메인 컴포넌트
────────────────────────────────────────*/
interface ExcelFormEditModalProps {
  open: boolean
  onClose: () => void
  // 양식 조회/저장 시 백엔드가 키로 쓰는 url. 미지정 시 현재 pathname 사용(기존 동작 유지).
  // flagged-review처럼 프런트 경로(/customer/flagged-review)와 백엔드 키(/customer-issues)가
  // 다른 경우 명시적으로 넘긴다.
  url?: string
}

const ExcelFormEditModal = ({open, onClose, url}: ExcelFormEditModalProps) => {
  const excelFormUrl = url ?? window.location.pathname
  const isMobile = useIsMobile()
  const [chipData, setChipData] = React.useState<any[]>([])
  const [excelList, setExcelList] = useState<any>(null)
  const [selectOpen, setSelectOpen] = useState(false)
  const isSelectingRef = useRef(false)
  const officeInfo = useSelector((state: any) => state.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)

  // 모바일 터치 / 데스크탑 마우스 모두 지원
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(TouchSensor, {activationConstraint: {delay: 200, tolerance: 5}})
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return

    setChipData((prev) => {
      const oldIndex = prev.findIndex((c) => c.idx === active.id)
      const newIndex = prev.findIndex((c) => c.idx === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleDelete = (chipToDelete: any) => () => {
    setChipData((chips: any) => chips.filter((chip: any) => chip.idx !== chipToDelete.idx))
  }

  const handleAdd = (e: any) => {
    isSelectingRef.current = true
    const label = _.find(excelList, (el: any) => el?.idx === e.target.value)?.label
    setChipData((chips: any) => [...chips, {idx: e.target.value, label: label}])
  }

  const filteredExcelList = excelList?.filter((el: any) => {
    return !chipData.some((chip: any) => chip?.idx === el?.idx)
  })

  const getExcelForm = () => {
    axiosWithAuth()
      .get(`${envBackHost}/excel/form?url=${excelFormUrl}`)
      .then((res) => {
        setExcelList(res.data.res)
      })
  }

  const columns: GridColDef[] = _.map(chipData, (el) => ({
    field: el.label,
    headerName: el.label,
    width: el?.label?.length <= 3 ? 100 : el?.label?.length * 24,
    headerAlign: 'center',
  }))

  const rows = _.map(chipData, (el, i) => ({id: i, ...el}))

  const swapArrayElements = (arr: any[], index1: number, index2: number) => {
    ;[arr[index1], arr[index2]] = [arr[index2], arr[index1]]
  }

  const handleColumnOrderChange = (params: any) => {
    const newChipData = [...chipData]
    swapArrayElements(newChipData, params.oldIndex, params.targetIndex)
    setChipData(newChipData)
  }

  const getOfficeExcelForm = () => {
    axiosWithAuth()
      .get(`${envBackHost}/${userInfo?.userId}/excel/form?url=${excelFormUrl}`)
      .then((res) => {
        setChipData(res.data.res)
      })
  }

  const postExcelForm = async () => {
    if (chipData?.length === 0) {
      await validationAlert('선택된 항목이 없습니다.', 'warning')
      return
    }
    axiosWithAuth()
      .post(`${envBackHost}/${userInfo?.userId}/excel/order?url=${excelFormUrl}`, {
        excelOrderList: chipData,
      })
      .then(async (res) => {
        await resultAlert(res.data)
        if (res.data.resultCode === 0) {
          getExcelForm()
          onClose()
        }
      })
  }

  useEffect(() => {
    getExcelForm()
    getOfficeExcelForm()
  }, [])

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{backdrop: Backdrop}}
      slotProps={{backdrop: {timeout: 300}}}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: {xs: '92%', md: '80%'},
          maxWidth: 960,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          outline: 'none',
        }}
      >
        {/* 헤더 */}
        <div className='flex justify-between items-center px-6 py-4 border-b border-gray-200'>
          <span className='font-semibold text-[1.1rem]'>엑셀 양식지정</span>
          <CancelPresentationIcon
            sx={{fontSize: '36px', cursor: 'pointer'}}
            color={'error'}
            onClick={onClose}
          />
        </div>

        {/* 바디 */}
        <div className='px-6 py-5'>
          <div className='flex gap-2 mb-4'>
            <FormControl fullWidth>
              <InputLabel id='excel-col-label'>엑셀 열 추가</InputLabel>
              <Select
                labelId='excel-col-label'
                label='엑셀 열 추가'
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  if (!isSelectingRef.current) {
                    setSelectOpen(false)
                  }
                  isSelectingRef.current = false
                }}
                onChange={(e: any) => handleAdd(e)}
              >
                {filteredExcelList && filteredExcelList.length > 0 ? (
                  _.map(filteredExcelList, (el: any, i: number) => {
                    if (el?.idx === 37 && HyphensOfficeHandler(officeInfo?.value) === false)
                      return false
                    if (
                      (el?.idx === 38 || el?.idx === 39 || el?.idx === 40) &&
                      !outerInstallShowHandler(serverLocalName, officeInfo?.value)
                    )
                      return false
                    if (
                      el?.idx === 41 &&
                      !showBusanExcelHandler(serverLocalName, officeInfo?.value)
                    )
                      return false
                    return (
                      <MenuItem value={el?.idx} key={i}>
                        {el?.label}
                      </MenuItem>
                    )
                  })
                ) : (
                  <MenuItem value='' disabled>
                    선택할 수 있는 항목을 모두 선택하였습니다.
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <Button variant={'contained'} onClick={() => getOfficeExcelForm()}>
              <RefreshIcon />
            </Button>
          </div>

          {chipData?.length > 0 && (
            <>
              {/* 통합 헤더 */}
              <div className='flex gap-1 mb-2 items-center'>
                <span className='text-xl font-bold'>열 순서 지정</span>
                <Tooltip
                  title={
                    <div className='flex flex-col gap-1'>
                      <span style={{fontSize: '1.25rem'}}>
                        칩을 드래그하여 순서를 변경할 수 있습니다.
                      </span>
                      {!isMobile && (
                        <span style={{fontSize: '1.25rem'}}>
                          데스크탑에서는 아래 미리보기 열도 드래그할 수 있습니다.
                        </span>
                      )}
                    </div>
                  }
                  TransitionComponent={Fade}
                  TransitionProps={{timeout: 600}}
                  placement={'right-start'}
                  arrow={true}
                  componentsProps={{tooltip: {sx: {maxWidth: 420}}}}
                >
                  <HelpIcon color={'primary'} sx={{fontSize: '20px', cursor: 'pointer'}} />
                </Tooltip>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={chipData.map((c) => c.idx)} strategy={rectSortingStrategy}>
                  <Paper
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      listStyle: 'none',
                      m: 0,
                    }}
                    component='ul'
                  >
                    {chipData?.map((data: any) => (
                      <SortableChip key={data?.idx} data={data} onDelete={handleDelete(data)} />
                    ))}
                  </Paper>
                </SortableContext>
              </DndContext>

              {!isMobile && (
                <div className='mt-4'>
                  <div className='flex gap-1 mb-2 items-center'>
                    <span className='text-xl font-bold'>Excel 양식 미리보기</span>
                  </div>
                  <div style={{width: '100%', overflowX: 'auto'}}>
                    <div style={{minWidth: `${columns.length * 100}px`, height: '123px'}}>
                      <DataGridPro
                        columns={columns}
                        rows={rows}
                        disableColumnMenu
                        hideFooterRowCount={true}
                        pagination={false}
                        onColumnOrderChange={handleColumnOrderChange}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 푸터 */}
        <div className='flex justify-end gap-4 px-6 py-4 border-t border-gray-200'>
          <Button variant={'contained'} color={'error'} onClick={onClose}>
            취소
          </Button>
          <Button
            variant={'contained'}
            color={'success'}
            disabled={chipData?.length === 0}
            onClick={() => postExcelForm()}
          >
            저장
          </Button>
        </div>
      </Box>
    </Modal>
  )
}

export default ExcelFormEditModal
